import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { formatPrice } from "@/utils/currency";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Copy, Check, Eye, EyeOff, Trash2, Pencil, BarChart3, ChevronDown } from "lucide-react";
import { DelivererAccessButtons } from "@/components/deliverers/DelivererAccessButtons";

export function MotorizadoDetail({ deliverer, onBack, onRefresh }) {
  const { t } = useTranslation();
  const { authAxios } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newToken, setNewToken] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [copiedNew, setCopiedNew] = useState(false);

  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    authAxiosRef.current.get(`/deliverers/${deliverer.id}/stats`)
      .then(res => { setStats(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [deliverer.id]);

  const copyNewToken = () => {
    navigator.clipboard.writeText(newToken);
    setCopiedNew(true);
    setTimeout(() => setCopiedNew(false), 2000);
  };

  const regenerateToken = async () => {
    try {
      const res = await authAxios.patch(`/deliverers/${deliverer.id}/regenerate-token`);
      setNewToken(res.data.token || null);
      setRevealed(false);
      toast.success(t("motorizado.tokenRegenerated"));
    } catch { toast.error(t("motorizado.errorGeneric")); }
  };

  const deleteDeliverer = async () => {
    if (!window.confirm(t("motorizado.deleteConfirm", { name: deliverer.name }))) return;
    try {
      await authAxios.delete(`/deliverers/${deliverer.id}`);
      toast.success(t("motorizado.deleted"));
      onBack();
      onRefresh();
    } catch { toast.error(t("motorizado.errorGeneric")); }
  };

  const costFields = [
    { label: t("motorizado.costPerHour"), value: deliverer.cost_per_hour, isPercent: false },
    { label: t("motorizado.costPerOrder"), value: deliverer.cost_per_delivery, isPercent: false },
    { label: t("motorizado.costPerKm"), value: deliverer.cost_per_km, isPercent: false },
    { label: t("motorizado.shippingPercent"), value: deliverer.shipping_percent, isPercent: true },
    { label: t("motorizado.minPerOrder"), value: deliverer.min_per_delivery, isPercent: false },
    { label: t("motorizado.minPerDay"), value: deliverer.min_per_day, isPercent: false },
  ];

  const statusBadge = (status) => {
    if (status === "available") return { label: t("motorizado.available"), cls: "bg-emerald-100 text-emerald-700" };
    if (status === "delivering") return { label: t("motorizado.delivering"), cls: "bg-purple-100 text-purple-700" };
    return { label: t("motorizado.offline"), cls: "bg-gray-100 text-gray-500" };
  };

  const hasAnyCost = costFields.some(f => f.value);
  const opDeliverer = stats?.deliverer || deliverer;
  const badge = statusBadge(opDeliverer.status);
  const formatSeen = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <div data-testid="motorizado-detail" className="pb-24">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm mb-4 flex-wrap">
        <button className="text-emerald-700 hover:underline" onClick={onBack}>{t("motorizado.breadcrumbHome")}</button>
        <span className="text-gray-400">/</span>
        <button className="text-emerald-700 hover:underline" onClick={onBack}>{t("motorizado.breadcrumbList")}</button>
        <span className="text-gray-400">/</span>
        <span className="font-medium truncate max-w-[140px]">{deliverer.name}</span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 mb-6">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700 font-bold gap-2">
              {t("motorizado.actions")} <ChevronDown className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem><Pencil className="w-4 h-4 mr-2" /> {t("motorizado.actionEdit")}</DropdownMenuItem>
            <DropdownMenuItem onClick={regenerateToken}><Copy className="w-4 h-4 mr-2" /> {t("motorizado.actionRegenToken")}</DropdownMenuItem>
            <DropdownMenuItem onClick={deleteDeliverer} className="text-red-600">
              <Trash2 className="w-4 h-4 mr-2" /> {t("motorizado.actionDelete")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Token */}
      <div className="bg-white rounded-lg border p-4 mb-6">
        <Label className="text-xs uppercase text-gray-500 font-semibold">{t("motorizado.tokenLabel")}</Label>
        {newToken ? (
          <div>
            <div className="flex items-center gap-2 mt-2">
              <code className="flex-1 bg-gray-50 border rounded px-3 py-2 text-sm font-mono break-all min-w-0">
                {revealed ? newToken : `${newToken.slice(0, 4)}${"•".repeat(24)}${newToken.slice(-4)}`}
              </code>
              <Button
                variant="outline" size="icon" className="shrink-0"
                onClick={() => setRevealed(r => !r)}
                aria-label={revealed ? "Ocultar token" : "Revelar token"}
              >
                {revealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
              {revealed && (
                <Button variant="outline" size="icon" onClick={copyNewToken} data-testid="copy-token-detail" className="shrink-0">
                  {copiedNew ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1">{t("motorizado.tokenHint")}</p>
          </div>
        ) : (
          <p className="text-sm text-gray-500 mt-2 italic" data-testid="token-hidden-msg">
            Token oculto por segurança. Gere um novo token para copiar.
          </p>
        )}
      </div>

      {/* WA Access */}
      <div className="flex flex-wrap gap-2 mb-4">
        <DelivererAccessButtons delivererId={deliverer.id} phone={deliverer.phone} />
      </div>

      {/* Cost fields — only rendered when at least one value is configured */}
      {hasAnyCost && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
          {costFields.map(item => (
            <div key={item.label} className="bg-white rounded-lg border p-3">
              <p className="text-xs text-gray-500 uppercase leading-tight mb-1">{item.label}</p>
              {item.isPercent ? (
                <p className="text-lg font-bold">{item.value ? `${item.value}%` : <span className="text-sm font-normal text-gray-400">{t("motorizado.notConfigured")}</span>}</p>
              ) : (
                <p className="text-lg font-bold">
                  {item.value
                    ? formatPrice(item.value)
                    : <span className="text-sm font-normal text-gray-400">{t("motorizado.notConfigured")}</span>}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Stats + Daily Orders */}
      {loading ? <LoadingScreen /> : stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Stats list */}
          <div>
            <h3 className="font-bold text-sm uppercase text-gray-700 mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" /> {t("motorizado.stats")}
            </h3>
            <div className="bg-white rounded-lg border overflow-hidden divide-y divide-gray-50">
              {[
                { label: t("motorizado.daysWorked"), value: stats.days_worked ?? "—" },
                { label: t("motorizado.totalOrders"), value: stats.total_orders ?? "—" },
                { label: t("motorizado.deliveredOrders"), value: stats.delivered_orders ?? "—", highlight: true },
                { label: t("motorizado.revenueGenerated"), value: stats.total_revenue != null ? formatPrice(stats.total_revenue) : "—" },
                { label: t("motorizado.gpsPoints"), value: stats.total_route_points ?? "—" },
              ].map((row, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-3 gap-3">
                  <span className="text-sm text-gray-600 flex-1 min-w-0">{row.label}</span>
                  <span className={`text-sm font-bold tabular-nums shrink-0 ${row.highlight ? "text-emerald-600" : ""}`}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Daily orders */}
          <div>
            <h3 className="font-bold text-sm uppercase text-gray-700 mb-3">{t("motorizado.ordersPerDay")}</h3>
            <div className="bg-white rounded-lg border p-4">
              {Object.keys(stats.daily_orders || {}).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(stats.daily_orders).sort().slice(-10).map(([day, count]) => (
                    <div key={day} className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 w-14 shrink-0">{day.slice(5)}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                        <div className="bg-emerald-500 h-full rounded-full" style={{ width: `${Math.min(100, count * 20)}%` }} />
                      </div>
                      <span className="text-xs font-bold w-5 text-right shrink-0">{count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-400 text-sm py-4">{t("motorizado.noOrderData")}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Info card */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm">
          <div className="min-w-[100px]">
            <span className="text-gray-500 text-xs uppercase block mb-0.5">{t("motorizado.phone")}</span>
            <p className="font-medium">{deliverer.phone}</p>
          </div>
          <div className="min-w-[80px]">
            <span className="text-gray-500 text-xs uppercase block mb-0.5">{t("motorizado.vehicle")}</span>
            <p className="font-medium capitalize">{deliverer.vehicle}</p>
          </div>
          <div className="min-w-[100px]">
            <span className="text-gray-500 text-xs uppercase block mb-0.5">{t("motorizado.statusLabel")}</span>
            <Badge className={badge.cls}>{badge.label}</Badge>
          </div>
          {formatSeen(opDeliverer.last_seen) && (
            <div className="min-w-[120px]">
              <span className="text-gray-500 text-xs uppercase block mb-0.5">Último acesso</span>
              <p className="font-medium tabular-nums">{formatSeen(opDeliverer.last_seen)}</p>
            </div>
          )}
          {opDeliverer.current_order_id && (
            <div className="min-w-[140px]">
              <span className="text-gray-500 text-xs uppercase block mb-0.5">Pedido atual</span>
              <span className="font-mono text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded border border-purple-200">
                #{opDeliverer.current_order_id.slice(-6).toUpperCase()}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
